
import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './History.css';

// เพิ่มสไตล์สำหรับ intro animation
const introStyles = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  background: 'rgba(244, 244, 244, 1)',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'opacity 0.7s',
  fontFamily: 'Prompt, sans-serif',
};

function History() {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [introStep, setIntroStep] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [completedTexts, setCompletedTexts] = useState({}); // เก็บข้อความที่พิมพ์เสร็จแล้ว

  useEffect(() => {
    setIsLoaded(true);
    
    // Handle window resize for responsive intro
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hide/show scrollbar based on intro state
  useEffect(() => {
    if (showIntro) {
      // Hide scrollbar
      document.body.style.overflow = 'hidden';
    } else {
      // Show scrollbar
      document.body.style.overflow = 'auto';
    }
    
    // Cleanup function to restore scrollbar when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showIntro]);

  const timelineData = [
    { year: "2517", title: t('history.event1Title'), content: t('history.event1Content') },
    { year: "2538", title: t('history.event2Title'), content: t('history.event2Content') },
    { year: "2552", title: t('history.event3Title'), content: t('history.event3Content') },
    { year: "2563", title: t('history.event4Title'), content: t('history.event4Content') },
    { year: t('history.event5Year'), title: t('history.event5Title'), content: t('history.event5Content') },
  ];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? timelineData.length - 1 : prev - 1));
  };
  const handleNext = () => {
    setCurrentIndex((prev) => (prev === timelineData.length - 1 ? 0 : prev + 1));
  };

  // Intro Animation Logic (แสดง intro 2 เหตุการณ์แรก)
  useEffect(() => {
    if (!showIntro) return;
    const totalSteps = timelineData.length; // Dynamic total steps based on data length
    let timer;
    
    if (introStep < totalSteps - 1) {
      // เพิ่มเวลาให้มากขึ้นเพื่อให้ typewriter animation ทำงานเสร็จ
      timer = setTimeout(() => setIntroStep(introStep + 1), 6500);
    } else if (introStep === totalSteps - 1) {
      // รอ 3 วินาทีก่อนเริ่ม fade out และใช้เวลา 1.5 วินาทีในการ fade out
      timer = setTimeout(() => {
        // เพิ่ม fade out class
        const introElement = document.querySelector('.intro-overlay');
        if (introElement) {
          introElement.style.transition = 'opacity 1.5s ease-out';
          introElement.style.opacity = '0';
          // รอให้ fade out เสร็จแล้วจึงซ่อน intro
          setTimeout(() => setShowIntro(false), 1500);
        } else {
          setShowIntro(false);
        }
      }, 7000); // เพิ่มเวลารอให้ typewriter animation เสร็จ
    }
    
    return () => clearTimeout(timer);
  }, [showIntro, introStep, timelineData.length]);

  // Custom hook สำหรับ typewriter effect
  const useTypewriter = (text, speed = 50, trigger = false, delay = 0) => {
    const [displayText, setDisplayText] = useState('');
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
      if (!trigger) {
        // เมื่อ trigger เป็น false แต่เคยพิมพ์เสร็จแล้ว ให้แสดงข้อความเต็ม
        if (isComplete && text) {
          setDisplayText(text);
        } else {
          setDisplayText('');
          setIsComplete(false);
        }
        return;
      }

      // ถ้า trigger เป็น true และยังไม่เคยพิมพ์เสร็จ
      if (!isComplete) {
        let timeoutId;
        
        // เริ่ม delay ก่อน
        const startDelay = setTimeout(() => {
          let currentIndex = 0;
          setDisplayText('');
          setIsComplete(false);

          const typeNextChar = () => {
            if (currentIndex < text.length) {
              setDisplayText(text.slice(0, currentIndex + 1));
              currentIndex++;
              timeoutId = setTimeout(typeNextChar, speed);
            } else {
              setIsComplete(true);
            }
          };

          typeNextChar();
        }, delay);

        return () => {
          clearTimeout(startDelay);
          clearTimeout(timeoutId);
        };
      } else {
        // ถ้าพิมพ์เสร็จแล้ว ให้แสดงข้อความเต็ม
        setDisplayText(text);
      }
    }, [text, speed, trigger, delay, isComplete]);

    return { displayText, isComplete };
  };

  // Animated Counter Hook
  // Easing function: easeOutQuad
  function easeOutQuad(t) {
    return t * (2 - t);
  }

  function useCountUp(target, start = 0, duration = 1200, trigger = true) {
    const [count, setCount] = useState(start);
    useEffect(() => {
      if (!trigger) return;
      let startTimestamp = null;
      function step(timestamp) {
        if (!startTimestamp) startTimestamp = timestamp;
        const rawProgress = Math.min((timestamp - startTimestamp) / duration, 1);
        const easedProgress = easeOutQuad(rawProgress);
        setCount(Math.floor(easedProgress * (target - start) + start));
        if (rawProgress < 1) {
          window.requestAnimationFrame(step);
        } else {
          setCount(target);
        }
      }
      window.requestAnimationFrame(step);
      // eslint-disable-next-line
    }, [target, start, duration, trigger]);
    return count;
  }

  // Intersection Observer for stats section
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = React.useRef(null);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const count1 = useCountUp(50, 0, 18000, statsInView);
  const count2 = useCountUp(100, 0, 15200, statsInView);
  const count3 = useCountUp(32, 0, 16500, statsInView);

  // CountUp animations for intro years
  const year2517Count = useCountUp(2517, 2497, 0, introStep === 0);
  const year2538Count = useCountUp(2538, 2517, 1000, introStep === 1);
  const year2552Count = useCountUp(2552, 2538, 1000, introStep === 2);
  const year2563Count = useCountUp(2563, 2552, 1000, introStep === 3);

  // Typewriter animations for each timeline element separately
  const element0TitleTypewriter = useTypewriter(timelineData[0]?.title || '', 60, introStep === 0, 800);
  const element0ContentTypewriter = useTypewriter(timelineData[0]?.content || '', 30, element0TitleTypewriter.isComplete, 200);
  
  const element1TitleTypewriter = useTypewriter(timelineData[1]?.title || '', 60, introStep === 1, 800);
  const element1ContentTypewriter = useTypewriter(timelineData[1]?.content || '', 30, element1TitleTypewriter.isComplete, 200);
  
  const element2TitleTypewriter = useTypewriter(timelineData[2]?.title || '', 60, introStep === 2, 800);
  const element2ContentTypewriter = useTypewriter(timelineData[2]?.content || '', 30, element2TitleTypewriter.isComplete, 200);
  
  const element3TitleTypewriter = useTypewriter(timelineData[3]?.title || '', 60, introStep === 3, 800);
  const element3ContentTypewriter = useTypewriter(timelineData[3]?.content || '', 40, element3TitleTypewriter.isComplete, 200);
  
  const element4TitleTypewriter = useTypewriter(timelineData[4]?.title || '', 60, introStep === 4, 800);
  const element4ContentTypewriter = useTypewriter(timelineData[4]?.content || '', 30, element4TitleTypewriter.isComplete, 200);

  // Combine typewriter animations into array
  const typewriterAnimations = [
    { titleTypewriter: element0TitleTypewriter, contentTypewriter: element0ContentTypewriter },
    { titleTypewriter: element1TitleTypewriter, contentTypewriter: element1ContentTypewriter },
    { titleTypewriter: element2TitleTypewriter, contentTypewriter: element2ContentTypewriter },
    { titleTypewriter: element3TitleTypewriter, contentTypewriter: element3ContentTypewriter },
    { titleTypewriter: element4TitleTypewriter, contentTypewriter: element4ContentTypewriter },
  ];

  // Function สำหรับคำนวณตำแหน่งและ opacity ของแต่ละ element ใน intro animation
  const getIntroElementStyle = (elementIndex, totalElements) => {
    const isMobile = windowWidth <= 768;
    const minWidth = isMobile ? '160vw' : '120vw';
    
    const positions = {
      0: ['50%', '0%', '-50%', '-50%', '-50%'], // 2517
      1: ['100%', '50%', '0%', '-50%', '-50%'], // 2538
      2: ['120%', '100%', '50%', '0%', '-40%'], // 2552
      3: ['120%', '120%', '100%', '50%', '-10%'], // 2563
      4: ['120%', '120%', '120%', '100%', '50%'] // ปัจจุบัน
    };

    const opacities = {
      0: [1, 0.5, 0, 0, 0], // 2517
      1: [0.5, 1, 0.5, 0, 0], // 2538
      2: [0, 0.5, 1, 0.5, 0], // 2552
      3: [0, 0, 0.5, 1, 0.5], // 2563
      4: [0, 0, 0, 0.5, 1] // ปัจจุบัน
    };

    // ตรวจสอบว่าเป็น element ที่อยู่กลางหรือไม่ (opacity = 1)
    const isCenter = opacities[elementIndex][introStep] === 1;
    const currentOpacity = opacities[elementIndex][introStep] || 0;
    
    // กำหนด blur สำหรับ element ที่ไม่ใช่ตัวกลาง
    let blurAmount = '0px';
    if (currentOpacity > 0 && !isCenter) {
      blurAmount = currentOpacity === 0.5 ? '3px' : '5px';
    }

    return {
      position: 'absolute',
      top: '50%',
      left: positions[elementIndex][introStep] || '150%',
      transform: 'translate(-50%, -50%)',
      transition: 'all 1s ease-out',
      zIndex: totalElements - elementIndex,
      minWidth: minWidth,
      opacity: currentOpacity,
      filter: `blur(${blurAmount})`,
    };
  };

  // Function สำหรับ render intro element
  const renderIntroElement = (data, elementIndex, typewriterData) => {
    // Responsive font sizes based on current window width
    const isMobile = windowWidth <= 768;
    const isTablet = windowWidth <= 1024 && windowWidth > 768;
    
    const yearFontSize = isMobile ? '6vw' : isTablet ? '4.5vw' : '3.5vw';
    const titleFontSize = isMobile ? '3vw' : isTablet ? '3vw' : '2.2vw';
    const descFontSize = isMobile ? '2.5vw' : isTablet ? '1.8vw' : '1.2vw';
    const maxWidth = isMobile ? '70vw' : isTablet ? '40vw' : '22vw';
    const marginBottom1 = isMobile ? '1.5vw' : isTablet ? '1.2vw' : '0.8vw';
    const marginBottom2 = isMobile ? '1.2vw' : isTablet ? '1vw' : '0.6vw';
    
    // Define opacities object (same as in getIntroElementStyle)
    const opacities = {
      0: [1, 0.5, 0, 0, 0], // 2517
      1: [0.5, 1, 0.5, 0, 0], // 2538
      2: [0, 0.5, 1, 0.5, 0], // 2552
      3: [0, 0, 0.5, 1, 0.5], // 2563
      4: [0, 0, 0, 0.5, 1] // ปัจจุบัน
    };
    
    // ตรวจสอบว่า element นี้กำลัง active หรือไม่ (เพื่อเริ่ม animation)
    const isActive = opacities[elementIndex][introStep] === 1;
    
    // กำหนดค่าปีที่จะแสดง โดยใช้ countUp animation หรือแสดงปกติ
    let displayYear = data.year;
      switch(elementIndex) {
        case 0: displayYear = year2517Count; break;
        case 1: displayYear = year2538Count; break;
        case 2: displayYear = year2552Count; break;
        case 3: displayYear = year2563Count; break;
        default: displayYear = data.year;
      }

    // รับ typewriter data จาก parameter
    const { titleTypewriter, contentTypewriter } = typewriterData;
    
    return (
      <div
        key={`intro-${elementIndex}`}
        style={getIntroElementStyle(elementIndex, timelineData.length)}
      >
        <div style={{ fontSize: yearFontSize, fontWeight: 700, color: '#e54646ff', marginBottom: marginBottom1, textAlign: 'center' }}>
          {displayYear}
        </div>
        <div style={{ fontSize: titleFontSize, fontWeight: 500, color: '#222', marginBottom: marginBottom2, textAlign: 'center', minHeight: titleFontSize }}>
          {titleTypewriter.displayText}
          {isActive && !titleTypewriter.isComplete && <span className="cursor">|</span>}
        </div>
        <div style={{ fontSize: descFontSize, color: '#444', maxWidth: maxWidth, margin: '0 auto', textAlign: 'center', minHeight: `calc(${descFontSize} * 1.5)` }}>
          {contentTypewriter.displayText}
          {titleTypewriter.isComplete && !contentTypewriter.isComplete && <span className="cursor">|</span>}
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      {/* Intro Animation Overlay */}
      {showIntro && (
        <div
          className="intro-overlay"
          style={{
            ...introStyles,
            opacity: showIntro ? 1 : 0,
            pointerEvents: showIntro ? 'auto' : 'none',
            overflow: 'hidden',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
          }}
        >
          {/* Skip Button */}
          <button
            onClick={() => setShowIntro(false)}
            style={{
              position: 'absolute',
              top: '24px',
              right: '32px',
              padding: '10px 24px',
              backgroundColor: '#e54646ff',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1.1rem',
              fontWeight: 500,
              cursor: 'pointer',
              zIndex: 10000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
            aria-label="Skip intro animation"
          >
            {t('history.skipIntro')}
          </button>
          {/* Multi-step intro timeline */}
          <div style={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: 'background 0.7s',
          }}>
            {/* Render ทุก elements ด้วย function */}
            {timelineData.map((data, index) => renderIntroElement(data, index, typewriterAnimations[index]))}
          </div>
        </div>
      )}
      {/* Main Content */}
      <div className={`history-page ${isLoaded ? 'loaded' : ''}`} style={{ fontFamily: "Prompt, sans-serif", position: "relative", padding: "15vw 0" }}>
        <div className="history-bg-text">HISTORY</div>
        <Container className="py-5">
          {/* <h1 className="history-title text-center mb-5">บริษัท สำนักงานอรอนงค์การบัญชีและสอบบัญชี จำกัด</h1> */}
          <div className="timeline-carousel-container">
            <button className="timeline-arrow left" onClick={handlePrev} aria-label="Previous">&#8249;</button>
            <div className="timeline-carousel">
              {timelineData.map((item, idx) => (
                <div
                  key={idx}
                  className={`timeline-carousel-item${idx === currentIndex ? ' active' : ''}`}
                >
                  <div className="timeline-circle">
                    <span className="timeline-year">{item.year}</span>
                  </div>
                  <div className="timeline-line"></div>
                  <div className="timeline-info">
                    <h3 className="timeline-event-title">{item.title}</h3>
                    <p className="timeline-event-desc">{item.content}</p>
                  </div>
                </div>
              ))}
            </div>
            <button className="timeline-arrow right" onClick={handleNext} aria-label="Next">&#8250;</button>
          </div>
          <div className="timeline-pagination">
            {timelineData.map((_, idx) => (
              <span key={idx} className={`timeline-dot${idx === currentIndex ? ' active' : ''}`}></span>
            ))}
          </div>
          {/* Stats Section */}
          <div ref={statsRef} className="stats-section" style={{ display: "flex", justifyContent: "center", gap: "2vw", marginTop: "4vw" }}>
            <div style={{ background: "#fff", borderRadius: "1vw", boxShadow: "0 0.1vw 1vw rgba(0,0,0,0.07)", padding: "2vw 2.5vw", minWidth: "14vw", textAlign: "center" }}>
              <div style={{ fontSize: "2vw", fontWeight: "bold", color: "#e54646ff" }}>{count1}+ </div>
              <div style={{ fontSize: "0.9vw", color: "#555", marginTop: "0.5vw" }}>{t('history.statYearsLabel')}</div>
            </div>
            <div style={{ background: "#fff", borderRadius: "1vw", boxShadow: "0 0.1vw 1vw rgba(0,0,0,0.07)", padding: "2vw 2.5vw", minWidth: "14vw", textAlign: "center" }}>
              <div style={{ fontSize: "2vw", fontWeight: "bold", color: "#e54646ff" }}>{count2}+ </div>
              <div style={{ fontSize: "0.9vw", color: "#555", marginTop: "0.5vw" }}>{t('history.statCompaniesLabel')}</div>
            </div>
            <div style={{ background: "#fff", borderRadius: "1vw", boxShadow: "0 0.1vw 1vw rgba(0,0,0,0.07)", padding: "2vw 2.5vw", minWidth: "14vw", textAlign: "center" }}>
              <div style={{ fontSize: "2vw", fontWeight: "bold", color: "#e54646ff" }}>{count3}</div>
              <div style={{ fontSize: "0.9vw", color: "#555", marginTop: "0.5vw" }}>{t('history.statServicesLabel')}</div>
            </div>
          </div>
        </Container>
      </div>
      {/* CSS keyframes สำหรับ fadeInUp และ cursor animation */}
      <style>{`
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        
        .cursor {
          animation: blink 1s infinite;
          color: #e54646ff;
          font-weight: bold;
          margin-left: 2px;
        }
      `}</style>
    </React.Fragment>
  );
}

export default History;
